import React, { memo, useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import DProgressBar from '@/components/Global/DProgressBar';
import '@/components/Global/DProgressBar/index.css';
import MainNav from '@/v3-dashboard/components/MainNav';
import { useUserStore } from '@/stores/user/userStore';
import AiChatbotIcon from '@/shared/icons/AiChatbotIcon';
import SlidingChatSidebar from '@/components/Global/SlidingChatSidebar';
import { getBaseUrl } from '@/utils/getBaseUrl';
import GlobalModals from '@/components/GlobalModals';
import { useConversationStore } from '@/hooks/useConversationStore';
import MobileNavDrawer from '@/v3-dashboard/components/MobileNavDrawer';
import TopBar from '@/v3-dashboard/components/TopBar/index.jsx';
import FeedbackRecordingModal from '@/components/FeedbackRecordingModal';

const LayoutMediumScreen = ({ children, title, progressBar }) => {
  const location = useLocation();
  const userData = useUserStore((state) => state.user);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isChatMinimized, setIsChatMinimized] = useState(false);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);

  const KB_ID = 'ca7ac107-9716-4ef3-a6b5-55ceab49f15a';
  const { currentConversation, setCurrentConversation } = useConversationStore(KB_ID, 'embed');

  // Track the initial conversation ID when chat opens (to prevent iframe reload during active session)
  const initialConversationIdRef = React.useRef(currentConversation?.id);

  // State to force iframe reload on reset (timestamp ensures unique URL)
  const [resetTimestamp, setResetTimestamp] = React.useState(Date.now());

  // Track the previous open state to detect when chat is opened/reopened
  const prevChatOpenRef = React.useRef(isChatOpen && !isChatMinimized);

  // Update the initial conversation ID ref when chat is about to open
  // This runs BEFORE render, ensuring the ref is set before iframe URL is calculated
  const isCurrentlyOpen = isChatOpen && !isChatMinimized;
  const wasPreviouslyClosed = !prevChatOpenRef.current;

  if (isCurrentlyOpen && wasPreviouslyClosed) {
    initialConversationIdRef.current = currentConversation?.id;
  }

  // Update the previous state after render
  React.useEffect(() => {
    prevChatOpenRef.current = isCurrentlyOpen;
  }, [isCurrentlyOpen]);

  // Listen for conversation creation events from the iframe
  useEffect(() => {
    const handleMessage = (event) => {
      // Handle conversation creation from iframe
      if (event.data.eventType === 'conversationCreated' && event.data.kbId === KB_ID) {
        setCurrentConversation({ id: event.data.conversationId });
        // DO NOT update initialConversationIdRef here - this would cause iframe reload
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [KB_ID, setCurrentConversation]);


  // Memoize the iframe src URL to prevent unnecessary re-renders
  // Use initialConversationIdRef to prevent iframe reload when conversation is created during active session
  const iframeSrc = useMemo(() => {
    const baseUrl = `${getBaseUrl()}embed/`;
    const params = new URLSearchParams({
      kb_id: 'ca7ac107-9716-4ef3-a6b5-55ceab49f15a',
      token: '5e5ff150-8321-43cc-b0ed-5f89b44ecf81',
      modeltype: 'gpt-4-omnimodel-mini',
      tabs: 'false',
      client_full_name: userData?.full_name || '',
      client_email: userData?.email || '',
      from_page: encodeURIComponent(window.location.href)
    });

    
    if (initialConversationIdRef.current) {
      params.append('conversation_id', initialConversationIdRef.current);
    }

    // Add reset timestamp to force iframe reload when reset is clicked
    // This ensures the URL changes and iframe reloads with fresh state
    params.append('_reset', resetTimestamp.toString());

    return `${baseUrl}?${params.toString()}`;
  }, [userData?.full_name, userData?.email, resetTimestamp, isChatOpen, isChatMinimized]);



  // Load chatbot script for dashboard
  useEffect(() => {
    // Check if script already exists
    const existingScript = document.querySelector('script[src*="bubble-embed.js?kb_id=ca7ac107-9716-4ef3-a6b5-55ceab49f15a"]');
    if (existingScript) return;

    // Create and append script
    // const script = document.createElement('script');
    // script.src = 'http://localhost:3000/bubble-embed.js?kb_id=38bfee9e-bfdc-496a-980d-39cc2e90d753&token=f5e94fd6-c1dc-4a2c-b38e-2f738e28c06d';
    // script.type = 'text/javascript';
    // script.async = true;
    // document.body.appendChild(script);

    return () => {
      // Cleanup on unmount
      const scriptToRemove = document.querySelector('script[src*="bubble-embed.js?kb_id=ca7ac107-9716-4ef3-a6b5-55ceab49f15a"]');
      if (scriptToRemove) scriptToRemove.remove();
    };
  }, []);

  const isChatbotDetailsRoute = useMemo(() => {
    const pathname = location.pathname;
    return /^\/chatbot\/[^/]+/.test(pathname) && pathname !== '/chatbot/create';
  }, [location.pathname]);

  const isVoiceDetailsRoute = useMemo(() => {
    const pathname = location.pathname;
    return /^\/voice\/[^/]+/.test(pathname) && pathname !== '/voice/create';
  }, [location.pathname]);

  const isAccountPausedRoute = useMemo(() => {
    const pathname = location.pathname;
    return /^\/account-paused/.test(pathname);
  }, [location.pathname]);

  // Check if we're on a chatbot or voice details route to determine if sidebar is shown
  const showsChatbotSidebar = isChatbotDetailsRoute || isVoiceDetailsRoute || isAccountPausedRoute;

  return (
    <>
      <main className='layout-medium-screen flex flex-col w-full max-w-full overflow-hidden transition-all duration-500 h-full'>
        <TopBar
          title={title}
          isChatOpen={isChatOpen}
          setIsChatOpen={setIsChatOpen}
          setIsChatMinimized={setIsChatMinimized}
          isMobileNavOpen={isMobileNavOpen}
          setIsMobileNavOpen={setIsMobileNavOpen}
        />

        {/* Mobile Navigation Drawer - shown when burger menu is open on mobile */}
        <MobileNavDrawer isOpen={isMobileNavOpen} onClose={() => setIsMobileNavOpen(false)} />

        {/* Main content - hidden on mobile when burger menu is open */}
        {!isMobileNavOpen && (
          <div className="flex flex-row w-full h-full min-w-0 overflow-hidden ">
            {/* MainNav - hidden on mobile (under 768px), only visible on md and above, and hidden when ChatbotSidebar is shown */}
            {!showsChatbotSidebar && (
              <div className="hidden md:block">
                <MainNav />
              </div>
            )}

            <div className="w-full flex flex-col h-full gap-v3-ms min-w-0 overflow-hidden">
              {progressBar && progressBar.length > 0 && (
                <div className='p-v3-md'>
                  <div className="progress-bar-wrapper">
                    <DProgressBar steps={progressBar} />
                  </div>
                </div>
              )}

              <div className="h-[1px] flex flex-col grow">{children}</div>
            </div>
          </div>
        )}
      </main>

      {isChatOpen && !isChatMinimized && (
        <SlidingChatSidebar
          isOpen={isChatOpen}
          onClose={() => setIsChatOpen(false)}
          onMinimize={() => setIsChatMinimized(true)}
          onReset={() => {
            // Clear the initial conversation ID ref when reset is clicked
            initialConversationIdRef.current = null;
            // Update reset timestamp to force iframe URL change and reload
            setResetTimestamp(Date.now());
          }}
        >
          <div className="h-full w-full">
            <iframe src={iframeSrc} allow="clipboard-write; clipboard-read; ;microphone" width="100%" height="100%" frameBorder="0"></iframe>
          </div>
        </SlidingChatSidebar>
      )}

      {isChatOpen && isChatMinimized && (
        <div className="fixed bottom-6 right-6 z-50 bg-white shadow-lg rounded-full flex items-center px-4 py-2 border border-grey-10 cursor-pointer min-w-[120px] min-h-[48px]" onClick={() => setIsChatMinimized(false)}>
          <AiChatbotIcon className="w-4 h-4 text-purple-300 mr-2 mt-0.5" />
          <span className="font-medium text-grey-75 text-sm">Chat with us</span>
        </div>
      )}

      {/* Feedback Recording Modal */}
      <FeedbackRecordingModal />


      {/* Global Modals - includes Claude Models Modal */}
      <GlobalModals />
    </>
  );
};

// Use memo to prevent unnecessary re-renders
export default memo(LayoutMediumScreen); 